<template>
  <v-card>
    <v-card-title class="pb-0">
      <h1 class="headline">
        Редактирование Курьерской службы
      </h1>
    </v-card-title>
    <v-card-text class="pt-2">
      <v-form v-model="isValid">
        <v-layout column>
          <v-flex xs12>
            <v-layout row>
              <v-flex xs12>
                <TitleTextField
                  v-model="title"
                  :required="true"
                  :rules="[rules.required, rules.min]"
                  label="Название*"
                  class="mx-2 mb-3"
                  @input="isChanged = true"
                />
              </v-flex>
            </v-layout>

            <v-layout row>
              <v-flex xs6>
                <app-reference
                  v-model="contactPerson"
                  class="mx-2 mb-3"
                  label="Контактное лицо"
                  component-name="ContactPerson"
                  :return-object="true"
                  resource="contact-persons"
                  :outline="true"
                  :height-field="44"
                  @input="isChanged = true"
                />
              </v-flex>
              <v-flex xs6>
                <app-reference
                  v-model="address"
                  class="mx-2 mb-3"
                  resource="addresses"
                  component-name="Address"
                  label="Адрес"
                  :outline="true"
                  :height-field="44"
                  @input="isChanged = true"
                />
              </v-flex>
            </v-layout>

            <v-layout row>
              <v-flex xs6>
                <EditableList
                  v-model="phones"
                  class="mx-2 mb-3"
                  label="Телефон"
                  :mask="getMask('phone')"
                  :hint="hint"
                  filter="phoneInternal"
                  outline
                  height="44"
                  title="Редактирование номеров телефонов"
                  data-test="phone"
                  @change="changePhones"
                />
              </v-flex>
              <v-flex xs6>
                <EditableList
                  v-model="emails"
                  class="mx-2 mb-3"
                  label="Email"
                  :rules="[rules.email]"
                  outline
                  height="44"
                  title="Редактирование e-mail"
                  data-test="email"
                  @change="changeEmails"
                />
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>

        <v-layout class="mt-3">
          <v-btn
            small
            color="primary"
            :disabled="!(isValid && isChanged) || isRemoved || loading"
            @click="submit(false)"
          >
            Сохранить
          </v-btn>
          <v-btn
            v-if="!isModal"
            small
            color="primary"
            :disabled="!(isValid && isChanged) || isRemoved || loading"
            @click="saveAndClose"
          >
            Сохранить и закрыть
          </v-btn>
          <v-btn
            small
            @click="cancel"
          >
            Отмена
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="id && id!==0"
            small
            :color="isRemoved ? 'success' :'error'"
            @click="changeRemovedState"
          >
            {{ isRemoved ? "Восстановить" : "Удалить" }}
          </v-btn>
        </v-layout>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { NestedEditorsEventBus } from "../../components/nested-editors/nested-editors-event-bus.js";
import ConfirmMixin from "../../mixins/confirm-cancel-changes.js";
import * as courierServiceApi from "../../backend/courierServiceApi.js";

export default {
  mixins: [ConfirmMixin],
  props: ["id"],
  data() {
    return {
      isValid: false,
      isChanged: false,
      rules: {
        empty: (v) => !!v || "",
        required: (v) => !!v || "Поле обязательно к заполнению",
        min: (v) =>
          v.length >= 2 || "Поле должно содержать не менее 2-х символов",
        email: (v) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(v) || v === "" || "Не корректно введен e-mail.";
        },
      },
      resource: "courier-services",
      addMessage: "Курьерская служба добавлена",
      editMessage: "Курьерская служба изменена",
      title: "",
      contactPerson: null,
      address: null,
      phones: [],
      emails: [],
      isRemoved: false,
      hint: "Номер телефона уже добавлен",
      loading: false,
    };
  },
  computed: {
    isModal() {
      let parent = this.$parent;
      while (parent) {
        if (parent.$options._componentTag === "NestedEditors") {
          return true;
        }
        parent = parent.$parent;
      }
      return false;
    },
  },
  created() {
    if (+this.id) {
      this.getData();
    }
  },
  mounted() {
    if (this.isModal) {
      NestedEditorsEventBus.$emit("modal-editor-mounted");
    }
  },
  methods: {
    changePhones(val) {
      this.isChanged = true;
      this.phones = val;
    },
    changeEmails(val) {
      this.isChanged = true;
      this.emails = val;
    },
    getMask(controlType) {
      switch (controlType) {
        case "phone":
          return "+##(###)###-##-## *#####";
        default:
          return "";
      }
    },
    async getData() {
      const res = await courierServiceApi.getCourierService(
        this.resource,
        this.id,
      );
      this.title = res.data.Title;
      this.phones = res.data.Phones;
      this.emails = res.data.Emails;
      this.isRemoved = res.data.IsRemoved;
      this.contactPerson = res.data.ContactPerson
        ? {
          id: res.data.ContactPerson.Id,
          title: res.data.ContactPerson.Title,
        }
        : null;
      this.address = res.data.Address
        ? {
          id: res.data.Address.Id,
          title: res.data.Address.Title,
        }
        : null;
    },
    async submit(closeAfterSubmit) {
      const courierservice = {
        title: this.title,
        phones: this.phones,
        emails: this.emails,
        contactPersonId: this.contactPerson ? this.contactPerson.id : null,
        addressId: this.address ? this.address.id : null,
      };

      if (this.id && this.id > 0) {
        this.loading = true;
        await courierServiceApi.updateCourierService(
          this.resource,
          this.id,
          courierservice,
        );
        this.showInfo(this.editMessage);
        this.isChanged = false;
        this.loading = false;
        if (closeAfterSubmit) {
          this.$router.go(-1);
        }
        this.getData();
      } else {
        this.loading = true;
        const res = await courierServiceApi.createCourierService(
          this.resource,
          courierservice,
        );
        this.showInfo(this.addMessage);
        this.isChanged = false;
        this.loading = false;
        if (this.isModal) {
          NestedEditorsEventBus.$emit("create", res.data);
        } else {
          this.$router.replace(`/${this.resource}/${res.data}`);
          if (closeAfterSubmit) {
            this.$router.go(-1);
          }
        }
      }
    },
    cancel() {
      if (this.isModal) {
        NestedEditorsEventBus.$emit("close");
      } else {
        this.$router.go(-1);
      }
    },
    async saveAndClose() {
      await this.submit(true);
    },
    async changeRemovedState() {
      await courierServiceApi.changeRemovedState(
        this.resource,
        this.id,
        this.isRemoved,
      );
      this.isRemoved = !this.isRemoved;
    },
    resetLoaders() {
      this.loading = false;
    },
  },
};
</script>
